<template>
    <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
            <span class="text-subtitle-1">{{ title }}</span>
            <v-btn v-if="openInNewTab" :href="src" icon="mdi-open-in-new" rel="noopener" target="_blank"
                   variant="text"/>
        </v-card-title>

        <v-card-text class="py-0">
            <!--
              Sandboxed on purpose. `allow-same-origin` WITHOUT `allow-scripts`
              means the document cannot run any script at all, while this page
              can still reach into it to follow the reader's scroll. Dropping
              the sandbox, or rendering the markup with v-html, would make an
              uploaded document able to execute in our origin - and there is no
              sanitizer in this project.

              The white background matches the PDF viewer, which also renders
              its pages white in either theme.
            -->
            <iframe
                ref="frame"
                :src="src"
                :style="{ height, width: '100%', border: '1px solid #E0E0E0', background: '#fff' }"
                sandbox="allow-same-origin"
                title="Legal document"
                @load="onFrameLoad"
            ></iframe>

            <v-alert v-if="!scrolledToEnd" class="text-start" color="black" density="compact" type="info"
                     variant="text">Please scroll to the end to continue
            </v-alert>
        </v-card-text>

        <v-card-actions class="d-flex justify-end">
            <v-btn variant="outlined" size="small" @click="sendFeedback(0)">Decline</v-btn>
            <v-btn :disabled="!scrolledToEnd" color="success" size="small" @click="sendFeedback(1)">I Accept</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup>
import {ref, onBeforeUnmount} from 'vue';

/**
 * Continuous-scroll viewer for an HTML legal document.
 *
 * Mirrors PdfViewer's contract exactly - same props, same `signAction` payload -
 * so TakeConsent can swap between them on the document's `format` without
 * knowing anything else about either.
 *
 * The PDF viewer rasterises a fixed page layout, which readers have to zoom and
 * page through. HTML reflows to the viewport, so the document is simply one
 * scroll at a readable size.
 */
const props = defineProps({
    src: {required: true},        // blob: URL for the fetched document
    title: {type: String, default: 'Document'},
    height: {type: String, default: '600px'},
    openInNewTab: {type: Boolean, required: false},
});
const emits = defineEmits(['signAction']);

const frame = ref(null);
const scrolledToEnd = ref(false);
const timeSpent = ref(performance.now());
let win = null;

/** 24px of slack absorbs sub-pixel rounding and elastic scroll overshoot. */
const atEnd = (w) => {
    const doc = w.document.documentElement;
    const scrollable = doc.scrollHeight - w.innerHeight;
    // A document shorter than the frame has no end to reach, so it counts as
    // read as soon as it renders - otherwise Accept could never enable.
    return scrollable <= 24 || w.scrollY >= scrollable - 24;
};

const check = () => {
    if (scrolledToEnd.value || !win) return;
    try {
        if (atEnd(win)) scrolledToEnd.value = true;
    } catch {
        // Cross-origin or a torn-down frame: nothing readable, and the reader
        // must not be trapped behind a gate we cannot measure.
        scrolledToEnd.value = true;
    }
};

const onFrameLoad = () => {
    try {
        win = frame.value?.contentWindow ?? null;
        if (!win) {
            scrolledToEnd.value = true;
            return;
        }
        win.addEventListener('scroll', check, {passive: true});
        win.addEventListener('resize', check);
        // Late-loading content changes the height after the first measurement,
        // so re-check rather than trusting it.
        check();
        setTimeout(check, 600);
    } catch {
        // If the frame cannot be inspected at all, do not leave the reader
        // unable to consent - the document is on screen either way.
        scrolledToEnd.value = true;
    }
};

const sendFeedback = (action) => {
    const elapsed = parseFloat(((performance.now() - timeSpent.value) / 1000).toFixed(2));
    emits('signAction', {
        'accept': action,
        'scrolled_to_bottom': scrolledToEnd.value,
        time_spent: elapsed,
    });
};

onBeforeUnmount(() => {
    if (!win) return;
    try {
        win.removeEventListener('scroll', check);
        win.removeEventListener('resize', check);
    } catch {
        // Frame already gone.
    }
    win = null;
});
</script>
